// ============================================================================
// Session + identity.
//
// Magic link only — no passwords to reset, leak, or support. The creator types
// their email, gets a link, and is in. Supabase issues the JWT; RLS decides
// what that JWT can see. Nothing here is a security boundary: it drives what
// the UI shows, while the database independently enforces access.
// ============================================================================

import { supabase, errorMessage } from './supabase.js';

// Cached per page load so a dozen components don't each hit the network.
let cachedSession = null;
let cachedProfile = null;
let profilePromise = null;

/** Send the magic link. */
export async function sendMagicLink(email) {
  const clean = String(email ?? '').trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
    throw new Error('Please enter a valid email address');
  }

  // Deliberately the CURRENT origin, never rewritten: under PKCE the code
  // verifier is stored in this origin's localStorage, so the link must come
  // back to the same origin or the exchange fails and every retry loops.
  // Origin hygiene is handled upstream (lib/canonical-host.js hops off www
  // before this can run) and every legitimate origin — apex, www as a
  // safety net, pages.dev previews, localhost — is in the Supabase
  // allow-list (supabase/config.toml).
  const { error } = await supabase.auth.signInWithOtp({
    email: clean,
    options: {
      emailRedirectTo: `${window.location.origin}/#/auth/callback`,
      // Kept true so an approved creator can sign in the first time (approval
      // does not pre-provision an auth user). This also means a not-yet-approved
      // applicant who tries to sign in gets an auth user and a magic link, and
      // lands on the pending STATUS page after clicking it — which is fine.
      //
      // IMPORTANT: disable "Confirm email" in Supabase Auth settings. With it
      // ON, a brand-new address gets the "Confirm signup" template ("confirm
      // your email") whose link dead-ends at the callback. OFF, this sends the
      // Magic Link template, which the callback handles cleanly.
      shouldCreateUser: true
    }
  });

  if (error) throw new Error(errorMessage(error, 'Could not send the link. Try again.'));
  return clean;
}

export async function signOut() {
  await supabase.auth.signOut();
  cachedSession = null;
  cachedProfile = null;
  profilePromise = null;
  window.location.hash = '#/';
}

export async function getSession() {
  if (cachedSession !== null) return cachedSession;
  const { data } = await supabase.auth.getSession();
  cachedSession = data.session ?? false;
  return cachedSession;
}

export function currentUser() {
  return cachedSession ? cachedSession.user : null;
}

/**
 * The signed-in user's creator row, or null.
 *
 * Note this can legitimately be null for a signed-in user: someone who was
 * denied, or who signed in before applying. Callers must handle that rather
 * than assuming a session implies a profile.
 */
export async function getProfile({ refresh = false } = {}) {
  if (refresh) { cachedProfile = null; profilePromise = null; }
  if (cachedProfile) return cachedProfile;
  if (profilePromise) return profilePromise;

  profilePromise = (async () => {
    const session = await getSession();
    if (!session) return null;

    const { data, error } = await supabase
      .from('creators')
      .select('*, tier:tiers(*)')
      .eq('auth_user_id', session.user.id)
      .maybeSingle();

    if (error) {
      console.error('[auth] profile load failed', error);
      return null;
    }
    cachedProfile = data;
    return data;
  })();

  return profilePromise;
}

let cachedIsAdmin = null;
export async function isAdmin() {
  if (cachedIsAdmin !== null) return cachedIsAdmin;
  const session = await getSession();
  if (!session) { cachedIsAdmin = false; return false; }

  const { data, error } = await supabase.rpc('is_admin');
  cachedIsAdmin = !error && data === true;
  return cachedIsAdmin;
}

/** Drop caches — call after anything that changes points, tier, or status. */
export function invalidate() {
  cachedProfile = null;
  profilePromise = null;
}

/**
 * Consume a magic-link callback. Supabase's detectSessionInUrl handles the
 * token exchange; we just wait for it to settle and route onward.
 *
 * Links carrying a token_hash (staff invites from the invite-staff function)
 * are verified explicitly instead: admin-generated links have no PKCE
 * verifier, so this PKCE-configured client refuses their implicit-flow
 * fragment outright. verifyOtp works for any flow — and as a bonus the token
 * survives email link-scanners, which prefetch the page but never run this.
 */
export async function handleAuthCallback(params) {
  cachedSession = null;
  cachedIsAdmin = null;
  invalidate();

  const tokenHash = params?.get('token_hash');
  if (tokenHash) {
    const { error } = await supabase.auth.verifyOtp({
      type: params.get('type') || 'magiclink',
      token_hash: tokenHash
    });
    if (error) {
      return { ok: false, message: 'That sign-in link has expired. Request a new one.' };
    }
  }

  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) {
    return { ok: false, message: 'That sign-in link has expired. Request a new one.' };
  }
  cachedSession = data.session;
  return { ok: true };
}

// Keep the cache honest if the SDK refreshes or expires the token underneath us.
supabase.auth.onAuthStateChange((event, session) => {
  cachedSession = session ?? false;
  if (event === 'SIGNED_OUT') {
    cachedProfile = null;
    profilePromise = null;
    cachedIsAdmin = null;
  }
});
